/**
 * Own-account strings: password, two-factor, the "temporary password / required 2FA" gate, the e-mailed set-password page
 * and "forgot password" (`acct.*`), merged through `extra.ts`. `acctEn` is typed against `acctPl`.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const acctPl = {
  menu: 'Moje konto',
  title: 'Moje konto',
  subtitle: 'Hasło i logowanie dwuetapowe (2FA) — zabezpiecz swoje konto.',
  passwordTitle: 'Hasło',
  passwordSub: 'Zmiana hasła wylogowuje Cię na innych urządzeniach.',
  twoFactorTitle: 'Logowanie dwuetapowe (2FA)',
  twoFactorSub: 'Kod z aplikacji uwierzytelniającej (Google Authenticator, 1Password, Microsoft Authenticator…) przy każdym logowaniu.',
  currentPassword: 'Obecne hasło',
  newPassword: 'Nowe hasło',
  repeatPassword: 'Powtórz nowe hasło',
  reveal: 'Pokaż / ukryj hasło',
  passwordHint: 'Co najmniej {min} znaków; im dłuższe i bardziej zróżnicowane, tym lepiej.',
  mismatch: 'Hasła nie są takie same.',
  strength: { weak: 'Słabe', fair: 'Średnie', good: 'Dobre', strong: 'Mocne' },
  changePassword: 'Zmień hasło',
  passwordChanged: 'Hasło zostało zmienione.',
  err: {
    currentRequired: 'Podaj obecne hasło.',
    short: 'Hasło musi mieć co najmniej {min} znaków.',
    currentPassword: 'Obecne hasło jest nieprawidłowe.',
    unchanged: 'Nowe hasło musi się różnić od obecnego.',
    invalidCode: 'Nieprawidłowy kod. Sprawdź kod z aplikacji i spróbuj ponownie.',
    required: 'Administrator wymaga logowania dwuetapowego na tym koncie — nie można go wyłączyć.',
    invalidLink: 'Link jest nieprawidłowy albo wygasł.',
    tooMany: 'Zbyt wiele prób. Odczekaj chwilę i spróbuj ponownie.',
    network: 'Brak połączenia z serwerem.',
    generic: 'Nie udało się zapisać. Spróbuj ponownie.',
    codeLength: 'Kod ma 6 cyfr.',
  },
  on: 'Włączone',
  off: 'Wyłączone',
  enable: 'Włącz 2FA',
  disable: 'Wyłącz 2FA',
  twoFactorOn: 'Logowanie dwuetapowe jest włączone',
  twoFactorOff: 'Logowanie dwuetapowe wyłączone',
  twoFactorOffState: 'Logowanie dwuetapowe jest wyłączone',
  twoFactorOnBody: 'Przy logowaniu poprosimy o kod z aplikacji.',
  twoFactorOffBody: 'Włącz je, aby samo hasło nie wystarczało do wejścia na konto.',
  scan: 'Zeskanuj kod QR w aplikacji uwierzytelniającej, a potem wpisz wyświetlony w niej 6-cyfrowy kod.',
  qrAlt: 'Kod QR do aplikacji uwierzytelniającej',
  orManual: 'Albo wpisz ręcznie ten klucz:',
  copySecret: 'Kopiuj klucz',
  copied: 'Skopiowano',
  openApp: 'Otwórz w aplikacji na tym urządzeniu',
  code: 'Kod z aplikacji',
  codeToDisable: 'Aby wyłączyć, podaj aktualny kod z aplikacji',
  confirm: 'Potwierdź i włącz',
  gate: {
    passwordTitle: 'Ustaw własne hasło',
    passwordBody: 'Logujesz się hasłem tymczasowym. Zanim zaczniesz pracę, ustaw własne — to chwila.',
    twoFactorTitle: 'Włącz logowanie dwuetapowe',
    twoFactorBody: 'Administrator wymaga logowania dwuetapowego na Twoim koncie. Skonfiguruj je teraz, żeby przejść dalej.',
    currentLabel: 'Hasło tymczasowe',
  },
  link: {
    badTitle: 'Link jest nieważny',
    badBody: 'Link wygasł, został już użyty albo jest nieprawidłowy. Poproś administratora o nowy lub użyj „Nie pamiętasz hasła?” na ekranie logowania.',
    toLogin: 'Przejdź do logowania',
    doneTitle: 'Hasło ustawione',
    doneBody: 'Możesz się teraz zalogować nowym hasłem.',
    activationTitle: 'Witaj, {name}! Ustaw hasło',
    resetTitle: 'Ustaw nowe hasło',
    body: 'Wymyśl hasło, którego nie używasz nigdzie indziej.',
    save: 'Zapisz hasło',
  },
  forgot: {
    body: 'Podaj e-mail konta — jeśli istnieje, wyślemy link do ustawienia nowego hasła (ważny 2 godziny).',
    placeholder: 'E-mail konta',
    send: 'Wyślij link',
    sent: 'Jeśli konto istnieje, wysłaliśmy na ten adres link do ustawienia hasła. Sprawdź skrzynkę (także spam).',
  },
} as const;

export const acctEn: Shape<typeof acctPl> = {
  menu: 'My account',
  title: 'My account',
  subtitle: 'Password and two-factor sign-in — secure your account.',
  passwordTitle: 'Password',
  passwordSub: 'Changing the password signs you out on other devices.',
  twoFactorTitle: 'Two-factor sign-in (2FA)',
  twoFactorSub: 'A code from an authenticator app (Google Authenticator, 1Password, Microsoft Authenticator…) at every sign-in.',
  currentPassword: 'Current password',
  newPassword: 'New password',
  repeatPassword: 'Repeat the new password',
  reveal: 'Show / hide password',
  passwordHint: 'At least {min} characters; the longer and more varied, the better.',
  mismatch: 'The passwords do not match.',
  strength: { weak: 'Weak', fair: 'Fair', good: 'Good', strong: 'Strong' },
  changePassword: 'Change password',
  passwordChanged: 'Password changed.',
  err: {
    currentRequired: 'Enter your current password.',
    short: 'The password must be at least {min} characters long.',
    currentPassword: 'The current password is incorrect.',
    unchanged: 'The new password must differ from the current one.',
    invalidCode: 'Invalid code. Check the code in the app and try again.',
    required: 'An administrator requires two-factor sign-in on this account — it cannot be turned off.',
    invalidLink: 'The link is invalid or has expired.',
    tooMany: 'Too many attempts. Wait a moment and try again.',
    network: 'No connection to the server.',
    generic: 'Could not save. Try again.',
    codeLength: 'The code has 6 digits.',
  },
  on: 'On',
  off: 'Off',
  enable: 'Turn on 2FA',
  disable: 'Turn off 2FA',
  twoFactorOn: 'Two-factor sign-in is on',
  twoFactorOff: 'Two-factor sign-in turned off',
  twoFactorOffState: 'Two-factor sign-in is off',
  twoFactorOnBody: 'At sign-in we will ask for a code from the app.',
  twoFactorOffBody: 'Turn it on so that the password alone is not enough to get into the account.',
  scan: 'Scan the QR code in your authenticator app, then enter the 6-digit code it shows.',
  qrAlt: 'QR code for the authenticator app',
  orManual: 'Or type this key manually:',
  copySecret: 'Copy key',
  copied: 'Copied',
  openApp: 'Open in an app on this device',
  code: 'Code from the app',
  codeToDisable: 'To turn it off, enter the current code from the app',
  confirm: 'Confirm and turn on',
  gate: {
    passwordTitle: 'Set your own password',
    passwordBody: 'You signed in with a temporary password. Set your own before you start — it takes a moment.',
    twoFactorTitle: 'Turn on two-factor sign-in',
    twoFactorBody: 'An administrator requires two-factor sign-in on your account. Set it up now to continue.',
    currentLabel: 'Temporary password',
  },
  link: {
    badTitle: 'The link is not valid',
    badBody: 'The link has expired, was already used or is invalid. Ask an administrator for a new one or use “Forgot your password?” on the sign-in screen.',
    toLogin: 'Go to sign-in',
    doneTitle: 'Password set',
    doneBody: 'You can now sign in with the new password.',
    activationTitle: 'Welcome, {name}! Set a password',
    resetTitle: 'Set a new password',
    body: 'Pick a password you do not use anywhere else.',
    save: 'Save password',
  },
  forgot: {
    body: 'Enter the account e-mail — if it exists, we will send a link to set a new password (valid for 2 hours).',
    placeholder: 'Account e-mail',
    send: 'Send link',
    sent: 'If the account exists, we sent a link to set the password to that address. Check your inbox (and spam).',
  },
};
